'use client';

import { useQuery } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { MenuSnapshotProduct } from '@/features/totems/domain/model/menu-snapshot';
import { sortCategories } from '@/features/totems/domain/service/category-products';
import type { MenuDiscountCandidate } from '@/features/totems/domain/service/menu-discount';
import { shouldShowProduct } from '@/features/totems/domain/service/product-stock';
import type { TotemWithLocation } from '@/features/totems/presentation/actions/web-totem/get-totem-with-location';
import { useTotemCart } from '@/features/totems/presentation/hooks/web-totem/use-totem-cart';
import { useTotemContext } from '@/features/totems/presentation/hooks/web-totem/use-totem-context';
import { useTotemMenu } from '@/features/totems/presentation/hooks/web-totem/use-totem-menu';
import { menuDiscountsQueryOptions } from '@/features/totems/presentation/queries/totems.queries';

export type FlyToCartPayload = {
  imageUrl: string;
  fromRect: DOMRect;
};

/** Data of the menu being browsed: changes when the menu loads, a category is chosen or the user searches. */
export type TotemCatalogState = {
  totem: TotemWithLocation | null | undefined;
  totemLoading: boolean;
  menu: ReturnType<typeof useTotemMenu>['menu'];
  menuLoading: boolean;
  categories: ReturnType<typeof sortCategories>;
  activeCategoryId: string | null;
  activeCategoryName: string;
  searchQuery: string;
  /** Cupones vigentes por producto (informativos: se cobran tipeando el código en el carrito). */
  discountsByProduct: Record<string, MenuDiscountCandidate[]>;
};

/** Cart contents and the product being added or edited. */
export type TotemCartState = {
  cart: ReturnType<typeof useTotemCart>;
  selectedProduct: MenuSnapshotProduct | null;
  editingCartItemId: string | null;
  flyAnimation: FlyToCartPayload | null;
};

/** Checkout and dining options (where and how the order is served). */
export type TotemOrderState = {
  checkoutOpen: boolean;
  orderPanelOpen: boolean;
  orderOptionsOpen: boolean;
  orderType: 'takeaway' | 'eat_here' | null;
  pickupMethod: 'banderin' | 'beeper' | 'nombre' | null;
  beeperNumber: string;
};

export type TotemState = TotemCatalogState & TotemCartState & TotemOrderState;

/** Stable callbacks, setters and refs: they never change identity, so subscribing to them never re-renders. */
export type TotemActions = {
  setActiveCategoryId: (id: string) => void;
  setSelectedProduct: (product: MenuSnapshotProduct | null) => void;
  setEditingCartItemId: (id: string | null) => void;
  setCheckoutOpen: (open: boolean) => void;
  triggerFlyToCart: (payload: FlyToCartPayload) => void;
  clearFlyAnimation: () => void;
  desktopCheckoutRef: React.RefObject<HTMLElement | null>;
  mobileCheckoutRef: React.RefObject<HTMLElement | null>;
  setOrderPanelOpen: (open: boolean) => void;
  setOrderOptionsOpen: (open: boolean) => void;
  setOrderType: (type: 'takeaway' | 'eat_here' | null) => void;
  setPickupMethod: (method: 'banderin' | 'beeper' | 'nombre' | null) => void;
  setBeeperNumber: (num: string) => void;
  setSearchQuery: (query: string) => void;
};

export type TotemContextValue = TotemState & TotemActions;

export const TotemCatalogCtx = createContext<TotemCatalogState | null>(null);
export const TotemCartCtx = createContext<TotemCartState | null>(null);
export const TotemOrderCtx = createContext<TotemOrderState | null>(null);
export const TotemActionsCtx = createContext<TotemActions | null>(null);

function useRequiredContext<T>(ctx: React.Context<T | null>, hookName: string): T {
  const value = useContext(ctx);
  if (!value) {
    throw new Error(`${hookName} debe usarse dentro de TotemProvider`);
  }
  return value;
}

/** Only the stable actions: components that just trigger changes (cards, buttons) do not re-render on state updates. */
export function useTotemActions() {
  return useRequiredContext(TotemActionsCtx, 'useTotemActions');
}

/** Menu, categories and search: does not re-render when the cart or checkout changes. */
export function useTotemCatalog() {
  return useRequiredContext(TotemCatalogCtx, 'useTotemCatalog');
}

/** Cart and selected product: does not re-render when the user searches or changes category. */
export function useTotemCartState() {
  return useRequiredContext(TotemCartCtx, 'useTotemCartState');
}

/** Checkout and dining options. */
export function useTotemOrder() {
  return useRequiredContext(TotemOrderCtx, 'useTotemOrder');
}

/** Every state slice together (re-renders on any change); prefer a slice hook when only part is needed. */
export function useTotemState(): TotemState {
  const catalog = useTotemCatalog();
  const cart = useTotemCartState();
  const order = useTotemOrder();
  return useMemo(() => ({ ...catalog, ...cart, ...order }), [catalog, cart, order]);
}

export function useOptionalTotem(): TotemContextValue | null {
  const catalog = useContext(TotemCatalogCtx);
  const cart = useContext(TotemCartCtx);
  const order = useContext(TotemOrderCtx);
  const actions = useContext(TotemActionsCtx);
  return useMemo(
    () => (catalog && cart && order && actions ? { ...catalog, ...cart, ...order, ...actions } : null),
    [catalog, cart, order, actions]
  );
}

export function useTotem(): TotemContextValue {
  const state = useTotemState();
  const actions = useTotemActions();
  return useMemo(() => ({ ...state, ...actions }), [state, actions]);
}

/** Mounts the four totem contexts; shared by the web totem provider and the public carta provider. */
export function TotemContexts({
  catalog,
  cart,
  order,
  actions,
  children,
}: {
  catalog: TotemCatalogState;
  cart: TotemCartState;
  order: TotemOrderState;
  actions: TotemActions;
  children: ReactNode;
}) {
  return (
    <TotemActionsCtx.Provider value={actions}>
      <TotemCatalogCtx.Provider value={catalog}>
        <TotemCartCtx.Provider value={cart}>
          <TotemOrderCtx.Provider value={order}>{children}</TotemOrderCtx.Provider>
        </TotemCartCtx.Provider>
      </TotemCatalogCtx.Provider>
    </TotemActionsCtx.Provider>
  );
}

type Props = {
  totemId: string;
  children: ReactNode;
};

export function TotemProvider({ totemId, children }: Props) {
  const { totem, isLoading: totemLoading } = useTotemContext(totemId);
  const { menu, isLoading: menuLoading } = useTotemMenu(totemId, totem?.active_menu_id);
  const { data: menuDiscounts } = useQuery(menuDiscountsQueryOptions(totem?.location_id ?? undefined));
  const cart = useTotemCart();
  const desktopCheckoutRef = useRef<HTMLElement>(null);
  const mobileCheckoutRef = useRef<HTMLElement>(null);

  const categories = useMemo(() => {
    if (!menu) return [];
    const validCategories = (menu.categories ?? []).filter(category => {
      return menu.products.some(
        product => shouldShowProduct(product) && product.product_category?.some(pc => pc.category_id === category.id)
      );
    });
    return sortCategories(validCategories);
  }, [menu]);

  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<MenuSnapshotProduct | null>(null);
  const [editingCartItemId, setEditingCartItemId] = useState<string | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [flyAnimation, setFlyAnimation] = useState<FlyToCartPayload | null>(null);
  const [orderPanelOpen, setOrderPanelOpen] = useState(false);

  const [orderOptionsOpen, setOrderOptionsOpen] = useState(false);
  const [orderType, setOrderType] = useState<'takeaway' | 'eat_here' | null>(null);
  const [pickupMethod, setPickupMethod] = useState<'banderin' | 'beeper' | 'nombre' | null>(null);
  const [beeperNumber, setBeeperNumber] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const handleSetActiveCategoryId = useCallback((id: string) => {
    setActiveCategoryId(id);
    setSearchQuery('');
  }, []);

  const prevItemCountRef = useRef(cart.itemCount);

  useEffect(() => {
    // Si agregamos items nuevos (la cantidad total de productos crece o pasa a ser > 0)
    if (cart.itemCount > prevItemCountRef.current) {
      setOrderPanelOpen(true);
    } else if (cart.itemCount === 0) {
      // Auto cerrar si nos quedamos sin items
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOrderPanelOpen(false);
    }
    prevItemCountRef.current = cart.itemCount;
  }, [cart.itemCount]);

  useEffect(() => {
    // Si no hay orderType seleccionado, mostramos el overlay automáticamente al iniciar
    if (!orderType) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOrderOptionsOpen(true);
    }
  }, [orderType]);

  useEffect(() => {
    const handleReset = () => {
      setOrderType(null);
      setPickupMethod(null);
      setBeeperNumber('');
      setOrderOptionsOpen(true);
      // Opcional: Si quisieras vaciar el carrito, puedes descomentar la siguiente línea
      // cart.clearCart();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('reset-totem-order', handleReset);
      return () => window.removeEventListener('reset-totem-order', handleReset);
    }
  }, []);

  const resolvedCategoryId = useMemo(() => {
    if (activeCategoryId && categories.some(c => c.id === activeCategoryId)) {
      return activeCategoryId;
    }
    return categories[0]?.id ?? null;
  }, [activeCategoryId, categories]);

  const activeCategoryName = useMemo(() => {
    return categories.find(c => c.id === resolvedCategoryId)?.name ?? '';
  }, [categories, resolvedCategoryId]);

  const triggerFlyToCart = useCallback((payload: FlyToCartPayload) => {
    setFlyAnimation(payload);
  }, []);

  const clearFlyAnimation = useCallback(() => {
    setFlyAnimation(null);
  }, []);

  const actions = useMemo<TotemActions>(
    () => ({
      setActiveCategoryId: handleSetActiveCategoryId,
      setSelectedProduct,
      setEditingCartItemId,
      setCheckoutOpen,
      triggerFlyToCart,
      clearFlyAnimation,
      desktopCheckoutRef,
      mobileCheckoutRef,
      setOrderPanelOpen,
      setOrderOptionsOpen,
      setOrderType,
      setPickupMethod,
      setBeeperNumber,
      setSearchQuery,
    }),
    [handleSetActiveCategoryId, triggerFlyToCart, clearFlyAnimation]
  );

  const catalogState = useMemo<TotemCatalogState>(
    () => ({
      totem,
      totemLoading,
      menu,
      menuLoading,
      categories,
      activeCategoryId: resolvedCategoryId,
      activeCategoryName,
      searchQuery,
      discountsByProduct: menuDiscounts ?? {},
    }),
    [
      totem,
      totemLoading,
      menu,
      menuLoading,
      categories,
      resolvedCategoryId,
      activeCategoryName,
      searchQuery,
      menuDiscounts,
    ]
  );

  const cartState = useMemo<TotemCartState>(
    () => ({ cart, selectedProduct, editingCartItemId, flyAnimation }),
    [cart, selectedProduct, editingCartItemId, flyAnimation]
  );

  const orderState = useMemo<TotemOrderState>(
    () => ({ checkoutOpen, orderPanelOpen, orderOptionsOpen, orderType, pickupMethod, beeperNumber }),
    [checkoutOpen, orderPanelOpen, orderOptionsOpen, orderType, pickupMethod, beeperNumber]
  );

  return (
    <TotemContexts catalog={catalogState} cart={cartState} order={orderState} actions={actions}>
      {children}
    </TotemContexts>
  );
}
